import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';

interface WithdrawalRequest {
    requestId: string;
    userId: string;
    userName: string;
    userEmail: string;
    iban: string;
    bic: string;
    siren: string;
    walletBalance: number;
    amount: number;
    requestedAt: string;
}

const fmt = (n: number) =>
    n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function BackOfficeWalletWithdrawals() {
    const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<{ id: string; action: 'sent' | 'reject' } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/admin/wallet-withdrawal-requests`, authHeaders())
            .then((res) => setRequests(res.data?.data ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleAction = async (req: WithdrawalRequest, action: 'sent' | 'reject') => {
        setBusyId(req.requestId);
        setError(null);
        try {
            const path = action === 'sent' ? 'mark-sent' : 'reject';
            await axios.post(
                `${CONFIG.apiUrl}/admin/wallet-withdrawal-requests/${req.userId}/${req.requestId}/${path}`,
                {},
                authHeaders()
            );
            setRequests((prev) => prev.filter((r) => r.requestId !== req.requestId));
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors du traitement de la demande.');
        } finally {
            setBusyId(null);
            setConfirm(null);
        }
    };

    const total = requests.reduce((s, r) => s + r.amount, 0);

    return (
        <div className="pt-[50px] flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                {[
                    { label: 'Demandes en attente', value: loading ? '—' : String(requests.length) },
                    { label: 'Montant total demandé', value: loading ? '—' : fmt(total) },
                ].map((kpi) => (
                    <Card key={kpi.label} extra="p-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{kpi.label}</p>
                        <p className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">{kpi.value}</p>
                    </Card>
                ))}
            </div>

            <Card extra="p-5">
                <p className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                    Demandes de retrait cagnotte
                </p>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
                )}

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Chargement...</div>
                ) : requests.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">Aucune demande de retrait en attente.</div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {requests.map((req) => (
                            <div
                                key={req.requestId}
                                className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 dark:border-navy-600 dark:bg-navy-700"
                            >
                                <div className="min-w-[240px]">
                                    <p className="font-semibold text-navy-700 dark:text-white">
                                        {req.userName || '—'}
                                    </p>
                                    <p className="text-xs text-gray-500">{req.userEmail}</p>
                                    <p className="mt-0.5 text-xs text-gray-500">
                                        Demandé le {new Date(req.requestedAt).toLocaleDateString('fr-FR')} · Solde
                                        cagnotte restant (hors montant en attente) :{' '}
                                        <span className="font-semibold text-orange-600">{fmt(req.walletBalance)}</span>
                                    </p>
                                    <p className="mt-0.5 text-xs text-gray-500">
                                        IBAN : <span className="font-mono">{req.iban || '—'}</span>
                                        {req.bic ? ` · BIC : ${req.bic}` : ''}
                                        {req.siren ? ` · SIREN : ${req.siren}` : ''}
                                    </p>
                                </div>

                                <div className="flex items-center gap-3">
                                    <span className="text-lg font-bold text-green-600">{fmt(req.amount)}</span>

                                    {confirm?.id === req.requestId ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-500">
                                                {confirm.action === 'sent'
                                                    ? 'Confirmer que le virement a été effectué ? (le solde a déjà été débité à la demande)'
                                                    : 'Refuser la demande ? (le montant sera recrédité au vendeur)'}
                                            </span>
                                            <button
                                                onClick={() => handleAction(req, confirm.action)}
                                                disabled={busyId === req.requestId}
                                                className={`rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 ${
                                                    confirm.action === 'sent'
                                                        ? 'bg-green-500 hover:bg-green-600'
                                                        : 'bg-red-500 hover:bg-red-600'
                                                }`}
                                            >
                                                {busyId === req.requestId ? '...' : 'Oui'}
                                            </button>
                                            <button
                                                onClick={() => setConfirm(null)}
                                                className="rounded-md bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-300"
                                            >
                                                Annuler
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setConfirm({ id: req.requestId, action: 'sent' })}
                                                className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 whitespace-nowrap"
                                            >
                                                Marquer comme envoyé
                                            </button>
                                            <button
                                                onClick={() => setConfirm({ id: req.requestId, action: 'reject' })}
                                                className="rounded-md bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-300"
                                            >
                                                Refuser
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
